import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

const PHASE_MS = 240;
const RAIL_EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Module-scoped so it survives TopBar's per-route remount — lets the rail
// stretch from the previously active tab to the new one on the fresh mount.
let persistedActiveIdx: number | null = null;

interface RailRect {
  left: number;
  width: number;
}

/**
 * Drives the active-tab "stretch" underline.
 *
 * The rail rests on the active tab; on a tab change its leading edge extends to
 * span both tabs, then the trailing edge catches up. Because TopBar remounts on
 * every route change, the previously active index is kept module-scoped so the
 * animation can replay from it on the fresh mount.
 *
 * The motion runs through the Web Animations API on `transform` (translateX +
 * scaleX) so it stays GPU-composited and keeps playing even while a heavy
 * destination page blocks the main thread.
 *
 * @param activeIdx index of the active tab, or -1 to hide the rail.
 * @returns refs to attach to each tab and the rail, plus the rail's rest rect.
 */
export function useStretchRail(activeIdx: number) {
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const railRef = useRef<HTMLSpanElement>(null);
  const [rail, setRail] = useState<RailRect>({ left: 0, width: 0 });

  const measure = useCallback((idx: number): RailRect | null => {
    const el = idx >= 0 ? tabRefs.current[idx] : null;
    if (!el) return null;
    return { left: el.offsetLeft, width: el.offsetWidth };
  }, []);

  useLayoutEffect(() => {
    if (activeIdx < 0) {
      setRail({ left: 0, width: 0 });
      persistedActiveIdx = activeIdx;
      return;
    }

    const newPos = measure(activeIdx);
    if (!newPos) return;

    const oldIdx = persistedActiveIdx;
    const oldPos =
      oldIdx != null && oldIdx >= 0 && oldIdx !== activeIdx
        ? measure(oldIdx)
        : null;

    // React owns the rail's resting position (the new tab).
    setRail(newPos);

    // No animation on first placement, when returning from a railless route,
    // or when the tabs aren't laid out (mobile hides the <nav>).
    if (!oldPos || !railRef.current || newPos.width === 0) {
      persistedActiveIdx = activeIdx;
      return;
    }

    // Express each phase as a transform relative to the resting (new) tab.
    // transform-origin is the left edge (set in CSS), so scaleX grows the width
    // and translateX moves the left edge.
    const bridgeLeft = Math.min(oldPos.left, newPos.left);
    const bridgeRight = Math.max(
      oldPos.left + oldPos.width,
      newPos.left + newPos.width,
    );
    const toTransform = (left: number, width: number) =>
      `translateX(${left - newPos.left}px) scaleX(${width / newPos.width})`;
    const animation = railRef.current.animate(
      [
        { transform: toTransform(oldPos.left, oldPos.width) },
        {
          transform: toTransform(bridgeLeft, bridgeRight - bridgeLeft),
          offset: 0.5,
          easing: RAIL_EASE,
        },
        { transform: 'translateX(0px) scaleX(1)', easing: RAIL_EASE },
      ],
      { duration: PHASE_MS * 2, easing: RAIL_EASE },
    );

    // Commit the new index next frame so StrictMode's double-invoke (which
    // cancels this animation in cleanup) doesn't collapse it into a no-op.
    const raf = requestAnimationFrame(() => {
      persistedActiveIdx = activeIdx;
    });

    return () => {
      animation.cancel();
      cancelAnimationFrame(raf);
    };
  }, [activeIdx, measure]);

  // Re-align the rail on resize / after the Kanit webfont loads (no animation).
  useEffect(() => {
    const realign = () => {
      if (activeIdx < 0) return;
      const target = measure(activeIdx);
      if (target) setRail(target);
    };
    window.addEventListener('resize', realign);
    if ('fonts' in document) {
      document.fonts.ready.then(realign);
    }
    return () => window.removeEventListener('resize', realign);
  }, [activeIdx, measure]);

  return { rail, railRef, tabRefs };
}
